import { useAssetVizInternal } from 'features/assetViz/AssetVizInternalProvider'
import { useSurfaceProps } from 'features/assetViz/objects/surfaces/util/SurfaceProvider'
import { createPolygonsForWrapping } from 'features/geometry'
import lodashClamp from 'lodash/clamp'
import polylabel from 'polylabel'
import * as React from 'react'
import {
  ComponentSurfaceWrappingMode,
  GeckoComponentCoords,
  GeckoComponentPolygon,
  GeckoComponentRect,
  GeckoFeature,
  GeckoMeasurement,
  PolygonOrientation,
} from 'types'

import { FeatureLabel3D } from './FeatureLabel3D'

/**
 * Renders the name for a given polygon.  If the polygon is split via wrapping
 * (around a component), it will render a label for each piece.
 * This function assumes polygons only wrap in one direction.
 */
export const PolygonFeatureLabel3D: React.FC<{
  feature: GeckoFeature
  polygonPath: GeckoComponentPolygon
  className?: string
}> = (props) => {
  const { bounds, wrapMode } = useSurfaceProps()
  const { show3D } = useAssetVizInternal()
  const { coordsToDisplayIn2d, coordsToDisplayIn3d } = React.useMemo(() => {
    const boundsXStart = bounds.x.start.num
    const boundsXSpan = bounds.x.delta.num
    const boundsYStart = bounds.y.start.num
    const boundsYSpan = bounds.y.delta.num

    const uvs = props.polygonPath.points
      .map((point) => {
        return [
          (point.x.toNumber(bounds.x.unit) - boundsXStart) / boundsXSpan,
          (point.y.toNumber(bounds.y.unit) - boundsYStart) / boundsYSpan,
        ]
      })
      .flat()

    const wrapsX =
      wrapMode === ComponentSurfaceWrappingMode.UnrolledComponentWrapsX ||
      wrapMode === ComponentSurfaceWrappingMode.ComponentWrapsX

    const wrapsY =
      wrapMode === ComponentSurfaceWrappingMode.UnrolledComponentWrapsY ||
      wrapMode === ComponentSurfaceWrappingMode.ComponentWrapsY

    const uvPolygons = createPolygonsForWrapping(
      uvs,
      props.polygonPath.pathDirectionX,
      props.polygonPath.pathDirectionY,
      wrapsX,
      wrapsY
    )

    const splitPolygons = uvPolygons.map((uvPolygon) => {
      const polyRet: { x: GeckoMeasurement; y: GeckoMeasurement }[] = []
      for (let i = 0; i < uvPolygon.length; i += 2) {
        polyRet.push({
          x: GeckoMeasurement.CreateFromValue(
            uvPolygon[i] * boundsXSpan + boundsXStart,
            bounds.x.unit
          ),
          y: GeckoMeasurement.CreateFromValue(
            uvPolygon[i + 1] * boundsYSpan + boundsYStart,
            bounds.y.unit
          ),
        })
      }

      return GeckoComponentPolygon.CreateFromPolygonalPlate(
        polyRet,
        bounds,
        wrapMode
      )
    })

    const coordsToDisplayIn2d = splitPolygons.map((polygon) =>
      getLabelPosition(polygon, bounds, true)
    )

    const possibleCoordsToDisplayIn3d = splitPolygons
      .map((polygon) => getLabelPosition(polygon, bounds, false))
      .filter((coord) => GeckoComponentRect.Contains(bounds, coord))
      .at(0)

    return {
      polygons: splitPolygons,
      coordsToDisplayIn2d,
      coordsToDisplayIn3d:
        possibleCoordsToDisplayIn3d ?? coordsToDisplayIn2d[0],
    }
  }, [
    bounds,
    props.polygonPath.pathDirectionX,
    props.polygonPath.pathDirectionY,
    props.polygonPath.points,
    wrapMode,
  ])

  return show3D ? (
    <FeatureLabel3D
      feature={props.feature}
      coords={coordsToDisplayIn3d}
      anchor={'center'}
      className={props.className}
    />
  ) : (
    <>
      {coordsToDisplayIn2d.map((coords) => (
        <FeatureLabel3D
          key={coords.toString()}
          feature={props.feature}
          coords={coords}
          anchor={'center'}
          className={props.className}
        />
      ))}
    </>
  )
}

/**
 * Gets the position for a label on a polygonal plate, bound to be inside the parent component if clampToParentBounds === true.
 *
 * clampsToComponentBounds exists to help us with 2d displays, but is not used in 3d displays.
 *
 * If the polygon is positively oriented, this will be the "visual center" (as provided by `polylabel`).
 * If the polygon is negatively oriented, this will be the center of the edge closest to the center of the parent component.
 *
 * NOTE: This function requires that non-wrapping polygons (which can be created using `createPolygonsForWrapping`
 *       are passed in. If a wrapping polygon is passed in, the behavior is undefined.
 */
const getLabelPosition = (
  nonWrappingPolygon: GeckoComponentPolygon,
  parentComponentBounds: GeckoComponentRect,
  clampToParentBounds: boolean
): GeckoComponentCoords => {
  const polygonOrientation =
    GeckoComponentPolygon.getPolygonOrientation(nonWrappingPolygon)

  const parentStartX = parentComponentBounds.start.x
  const parentEndX = parentComponentBounds.end.x
  const parentStartY = parentComponentBounds.start.y
  const parentEndY = parentComponentBounds.end.y

  if (polygonOrientation === PolygonOrientation.PositivelyOriented) {
    const minX = Math.min(
      ...nonWrappingPolygon.points.map((p) => p.x.toNumber(parentStartX.unit)),
      clampToParentBounds ? parentStartX.num : Number.POSITIVE_INFINITY
    )
    const minY = Math.min(
      ...nonWrappingPolygon.points.map((p) => p.y.toNumber(parentStartY.unit)),
      clampToParentBounds ? parentStartY.num : Number.POSITIVE_INFINITY
    )
    // positive points to help with the polylabel algorithm, which doesn't seem to like negative numbers
    const positiveGeoJsonPoints = nonWrappingPolygon.points.map(({ x, y }) => [
      lodashClamp(
        x.toNumber(parentEndX.unit),
        clampToParentBounds ? parentStartX.num : Number.NEGATIVE_INFINITY,
        clampToParentBounds ? parentEndX.num : Number.POSITIVE_INFINITY
      ) + Math.abs(minX),

      lodashClamp(
        y.toNumber(parentEndY.unit),
        clampToParentBounds ? parentStartY.num : Number.NEGATIVE_INFINITY,
        clampToParentBounds ? parentEndY.num : Number.POSITIVE_INFINITY
      ) + Math.abs(minY),
    ])
    const internalCenter = polylabel([positiveGeoJsonPoints], 0.1)
    return GeckoComponentCoords.CreateFromValues(
      parentComponentBounds.componentID,
      // undo -> positive shift
      internalCenter[0] - Math.abs(minX),
      nonWrappingPolygon.space.x.unit,
      // undo -> positive shift
      internalCenter[1] - Math.abs(minY),
      nonWrappingPolygon.space.y.unit
    )
  }

  const centerOfBounds = GeckoComponentCoords.CreateFromMeasurements(
    parentComponentBounds.componentID,
    parentComponentBounds.x.delta.scale(0.5),
    parentComponentBounds.y.delta.scale(0.5)
  )

  const edgeCenters = nonWrappingPolygon.points.map((coord, idx) => {
    const nextCoord =
      nonWrappingPolygon.points[(idx + 1) % nonWrappingPolygon.points.length]
    return GeckoComponentCoords.CreateFromValues(
      parentComponentBounds.componentID,
      (coord.x.num + nextCoord.x.num) / 2,
      nonWrappingPolygon.space.x.unit,
      (coord.y.num + nextCoord.y.num) / 2,
      nonWrappingPolygon.space.y.unit
    )
  })

  const { edgeCenter } = edgeCenters.reduce(
    (closest, currentValue) => {
      const distance = GeckoComponentCoords.DistanceBetween(
        centerOfBounds,
        currentValue
      )
      return distance.num < closest.distance
        ? { distance: distance.num, edgeCenter: currentValue }
        : closest
    },
    { distance: Infinity, edgeCenter: edgeCenters[0] }
  )

  return edgeCenter
}
